import { useLanguage } from '@/contexts/LanguageContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Bot, Zap, Target, BarChart3, Clock, DollarSign, Users, Shield } from 'lucide-react';

const AIAutomation = () => {
  const { language } = useLanguage();

  const automationServices = [
    {
      icon: <Bot className="w-8 h-8" />,
      title: "Document Automation Solutions",
      description: "Intelligent document processing, extraction, and workflow automation",
      features: ["OCR & Data Extraction", "Automated Workflows", "Compliance Tracking", "Integration APIs"],
      roi: "70% faster processing"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Virtual AI Agents for Call Centers",
      description: "24/7 intelligent customer service with human-like interactions",
      features: ["Natural Language Processing", "Multi-language Support", "CRM Integration", "Performance Analytics"],
      roi: "60% cost reduction"
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Process Optimization",
      description: "AI-driven analysis and optimization of business processes",
      features: ["Process Mining", "Bottleneck Detection", "Automated Reporting", "Predictive Analytics"],
      roi: "45% efficiency gain"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Intelligent Decision Support",
      description: "Data-driven insights and automated decision-making systems",
      features: ["Real-time Analytics", "Predictive Modeling", "Risk Assessment", "Performance Dashboards"],
      roi: "35% better decisions"
    }
  ];

  const useCases = [
    {
      industry: "Insurance",
      useCase: "Claims Processing Automation",
      result: "Reduced processing time from 14 days to 2 hours",
      savings: "$2.3M annually"
    },
    {
      industry: "Logistics",
      useCase: "Route Optimization & Tracking",
      result: "30% reduction in delivery times",
      savings: "$1.8M annually"
    },
    {
      industry: "Finance",
      useCase: "Fraud Detection & Prevention",
      result: "99.7% accuracy in fraud detection",
      savings: "$5.2M prevented losses"
    },
    {
      industry: "Telecom",
      useCase: "Customer Service Automation",
      result: "85% query resolution without human intervention",
      savings: "$3.1M operational costs"
    }
  ];

  const benefits = [
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Time Savings",
      value: "Up to 80%",
      description: "Reduction in manual tasks"
    },
    {
      icon: <DollarSign className="w-6 h-6" />,
      title: "Cost Reduction",
      value: "40-60%",
      description: "Lower operational expenses"
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Accuracy Improvement",
      value: "99.5%+",
      description: "Error-free processing"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Customer Satisfaction",
      value: "25%+",
      description: "Faster response times"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-br from-blue-500/5 via-background to-purple-500/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">
              <Bot className="w-4 h-4 mr-2" />
              AI Business Automation
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              AI Business Automation That Works 24/7
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-4xl mx-auto mb-8">
              Deploy intelligent automation solutions that eliminate repetitive tasks, 
              reduce costs by 60%, and free your team to focus on strategic growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700" asChild>
                <a href="/rpa-assessment">
                  Start Automation Assessment
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button size="lg" variant="outline">
                Watch Demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Overview */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Measurable Business Impact
            </h2>
            <p className="text-xl text-muted-foreground">
              Real results our clients achieve with AI automation
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="mx-auto mb-4 p-3 bg-blue-500/10 rounded-full w-fit">
                    {benefit.icon}
                  </div>
                  <div className="text-3xl font-bold text-blue-600 mb-2">{benefit.value}</div>
                  <CardTitle className="text-lg">{benefit.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>{benefit.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Automation Services */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Our AI Automation Solutions
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Comprehensive automation services designed to transform your business operations
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {automationServices.map((service, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 bg-blue-500/10 rounded-lg">
                      {service.icon}
                    </div>
                    <div>
                      <CardTitle className="text-xl">{service.title}</CardTitle>
                      <Badge variant="secondary" className="mt-1">{service.roi}</Badge>
                    </div>
                  </div>
                  <CardDescription className="text-base">{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Real-World Success Stories
            </h2>
            <p className="text-xl text-muted-foreground">
              See how our AI automation solutions deliver measurable results
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {useCases.map((useCase, index) => (
              <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <Badge variant="outline">{useCase.industry}</Badge>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-600">{useCase.savings}</div>
                    <div className="text-sm text-muted-foreground">Annual Savings</div>
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-2">{useCase.useCase}</h3>
                <p className="text-muted-foreground">{useCase.result}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Fast Implementation, Immediate Results
            </h2>
            <p className="text-xl text-muted-foreground">
              Get your AI automation up and running in 30-90 days
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
              <h3 className="text-xl font-semibold mb-2">Assessment & Planning</h3>
              <p className="text-muted-foreground">Identify automation opportunities and design custom solutions</p>
              <div className="text-sm text-blue-600 font-medium mt-2">Week 1-2</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
              <h3 className="text-xl font-semibold mb-2">Development & Testing</h3>
              <p className="text-muted-foreground">Build, test, and refine your AI automation systems</p>
              <div className="text-sm text-blue-600 font-medium mt-2">Week 3-8</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
              <h3 className="text-xl font-semibold mb-2">Deployment & Optimization</h3>
              <p className="text-muted-foreground">Launch automation and continuously optimize performance</p>
              <div className="text-sm text-blue-600 font-medium mt-2">Week 9-12</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Automate Your Business?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join 300+ companies saving millions with our AI automation solutions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary">
              Get Free Automation Audit
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600">
              View ROI Calculator
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AIAutomation;